import React from "react";
import { motion } from "framer-motion";
import { EDUCATION, CERTIFICATIONS, ACHIEVEMENTS } from "@/data/portfolio";
import { CERTIFICATIONS as CERTS_ID } from "@/constants/testIds";
import SectionHeading from "@/components/molecules/SectionHeading";
import { FiAward, FiBookOpen, FiExternalLink } from "react-icons/fi";

export default function EducationAndCerts() {
  return (
    <section id="education" data-testid={CERTS_ID.section} className="section relative">
      <div className="container-x">
        <SectionHeading
          overline="05 — Education & Credentials"
          title="Foundations & continuous learning."
          description="Engineering degree, professional certifications and selected achievements."
        />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Education */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-5 rounded-3xl border border-[color:var(--border)] bg-[color:var(--surface)] p-6 md:p-8"
          >
            <div className="flex items-center gap-2 mb-6 text-[color:var(--accent)]">
              <FiBookOpen />
              <span className="label-mono">// education</span>
            </div>
            {EDUCATION.map((e) => (
              <div key={e.id} className="space-y-2">
                <h3 className="font-display text-2xl">{e.degree}</h3>
                <p className="text-[color:var(--text-muted)] font-body">{e.university}</p>
                <p className="font-mono text-sm text-[color:var(--text-muted)]">
                  {e.dates} · {e.location}
                </p>
              </div>
            ))}
            <div className="mt-8">
              <div className="label-mono mb-3">// achievements</div>
              <ul className="space-y-2">
                {ACHIEVEMENTS.map((a, i) => (
                  <li key={i} className="flex gap-2 text-sm text-[color:var(--text)]">
                    <span className="text-[color:var(--accent)]">▸</span>
                    <span className="font-body text-[color:var(--text-muted)]">{a}</span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>

          {/* Certifications */}
          <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-4">
            {CERTIFICATIONS.map((c, i) => (
              <motion.a
                key={c.id}
                href={c.url || "#"}
                target={c.url ? "_blank" : undefined}
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                whileHover={{ y: -4 }}
                className="group rounded-3xl border border-[color:var(--border)] bg-[color:var(--surface)] p-6 hover:border-[color:var(--accent)]/40 transition flex flex-col justify-between min-h-[180px]"
              >
                <div className="flex items-start justify-between">
                  <div className="w-10 h-10 rounded-xl bg-[color:var(--accent)]/10 border border-[color:var(--accent)]/30 flex items-center justify-center text-[color:var(--accent)]">
                    <FiAward />
                  </div>
                  {c.url && (
                    <FiExternalLink className="text-[color:var(--text-muted)] group-hover:text-[color:var(--accent)] transition" />
                  )}
                </div>
                <div className="mt-6">
                  <h4 className="font-display text-lg leading-tight">{c.name}</h4>
                  <p className="mt-1 font-mono text-xs text-[color:var(--text-muted)]">
                    {c.issuer} · {c.year}
                  </p>
                </div>
              </motion.a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
